import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router'
import { useUser } from '../generic/hooks/useUser'
import useFrontEndConfig from '../generic/hooks/useFrontEndConfig/useFrontEndConfig'

const MainMenuPermissionGuard = ({ isSmall, setHideMenu, setHideMainMenuBar }) => {
  const { user } = useUser()
  const navigate = useNavigate()
  const location = useLocation()
  const navigationType = useNavigationType()
  const [frontEndConfig] = useFrontEndConfig()
  // RN2 — client gate for Product Acceptance (MOD): the existing fabricationOrderAcceptanceEnabled
  // flag from the front-end config.
  const acceptanceEnabled = !!(frontEndConfig && frontEndConfig.fabricationOrderAcceptanceEnabled)

  const userPermissionCodes = useMemo(() => {
    return new Set(user?.profile?.permissions?.map(permission => permission.code) || [])
  }, [user])

  const isUserAuthorized = useCallback((permissionCode) => {
    return userPermissionCodes.has(permissionCode)
  }, [userPermissionCodes])

  const isSuperAdmin = user?.profile?.code === 'SUPERADMIN'
  // Story 213 (C14.3, DR-3): the profile ADMIN reaches Camera control only.
  const isAdmin = user?.profile?.code === 'ADMIN'

  // Paths that appear as an explicit child route somewhere (whether or not the current user is
  // authorized for them). Sibling routes that happen to nest textually under another authorized
  // route (e.g. /quality/productAcceptance under /quality) must NOT be swept in by the generic
  // startsWith prefix check below — only routes that are genuinely unregistered detail pages of an
  // authorized feature (e.g. /resources/toolManager/:toolId) should benefit from that prefix rule.
  const explicitlyGatedPaths = useMemo(() => new Set(), [])

  const authorizedMainMenuEntries = useMemo(() => {
    const entries = []
    explicitlyGatedPaths.clear()
    const getRoutePaths = ({ path, paths }) => paths || [path]
    const registerGatedPaths = (childRoutes) => {
      childRoutes.forEach((childRoute) => getRoutePaths(childRoute).forEach((path) => explicitlyGatedPaths.add(path)))
    }

    const addRoute = (permissionCode, path, showInMainMenu = true, section = 'main') => {
      if (isUserAuthorized(permissionCode)) {
        entries.push({ firstPath: path, paths: [path], showInMainMenu, mainMenuItems: 1, section })
      }
    }

    const addGroupRoute = (permissionCode, fallbackPath, childRoutes, showInMainMenu = true, section = 'main') => {
      registerGatedPaths(childRoutes)
      if (!isUserAuthorized(permissionCode)) return

      const authorizedChildRoutes = childRoutes.filter(({ permissionCode: childPermissionCode, enabled = true }) => (
        enabled && isUserAuthorized(childPermissionCode)
      ))
      entries.push({
        firstPath: authorizedChildRoutes[0] ? getRoutePaths(authorizedChildRoutes[0])[0] : fallbackPath,
        paths: authorizedChildRoutes.length > 0
          ? [fallbackPath, ...authorizedChildRoutes.flatMap(getRoutePaths)]
          : [fallbackPath],
        showInMainMenu,
        mainMenuItems: authorizedChildRoutes.length || 1,
        section
      })
    }

    const addChildPermissionGroupRoute = (fallbackPath, childRoutes, showInMainMenu = true, section = 'main') => {
      registerGatedPaths(childRoutes)
      const authorizedChildRoutes = childRoutes.filter(({ permissionCode: childPermissionCode, enabled = true }) => (
        enabled && isUserAuthorized(childPermissionCode)
      ))
      if (authorizedChildRoutes.length === 0) return

      entries.push({
        firstPath: getRoutePaths(authorizedChildRoutes[0])[0] || fallbackPath,
        paths: authorizedChildRoutes.flatMap(getRoutePaths),
        showInMainMenu,
        mainMenuItems: authorizedChildRoutes.length,
        section
      })
    }
    addRoute('HOME', '/home')
    addRoute('MONITORING', '/monitoring')
    addRoute('PLANNING', '/planning')
    addGroupRoute('ORDERS', '/orders', [
      { permissionCode: 'ORDERS TAB', path: '/orders' },
      { permissionCode: 'FABRICATION ORDERS', path: '/fabricationOrders' }
    ])
    addGroupRoute('PROCESS', '/process', [
      { permissionCode: 'WORKFLOWS_MANAGER', paths: ['/process/workflow2', '/process/programs', '/process/processTraceability'] },
      { permissionCode: 'RULES_MANAGER', paths: ['/process/rules', '/process/formulas'] },
      { permissionCode: 'STORAGE', paths: ['/process/storage', '/process/storagePosition', '/process/storageTraceability'] }
    ])
    addGroupRoute('RESOURCES', '/resources', [
      { permissionCode: 'PART', path: '/resources/reference' },
      { permissionCode: 'RAW MATERIAL', path: '/resources/rawmaterialReference' },
      { permissionCode: 'FIXTURE', path: '/resources/fixtureReference' },
      { permissionCode: 'TOOLS', path: '/resources/toolManager' },
      { permissionCode: 'BOM_LIBRARY', path: '/resources/bomLibrary' }
    ])
    addGroupRoute('ANALYTICS', '/dataAnalytics', [
      { permissionCode: 'DASHBOARD_OEE', path: '/dataAnalytics/dashboard/oee' },
      { permissionCode: 'DASHBOARD_PRODUCTION', path: '/dataAnalytics/dashboard/production' },
      { permissionCode: 'DASHBOARD_PLANNING', path: '/dataAnalytics/dashboard/planning' },
      { permissionCode: 'DASHBOARD_TOOLING', path: '/dataAnalytics/dashboard/tooling' },
      { permissionCode: 'DASHBOARD_CUSTOMIZED', path: '/dataAnalytics/dashboard/customized' },
      // Proxied service dashboards (S22) live under this base path; registering it authorizes
      // /dataAnalytics/dashboard/proxied/<prefix> via isCurrentPathAllowed's startsWith check.
      { permissionCode: 'DASHBOARD_CUSTOMIZED', path: '/dataAnalytics/dashboard/proxied' },
      { permissionCode: 'PREDICTION', path: '/dataAnalytics/prediction' },
      { permissionCode: 'PRESET', path: '/dataAnalytics/reports/preset' },
      { permissionCode: 'INSPECTION', path: '/dataAnalytics/reports/inspection' },
      { permissionCode: 'QUALITY', path: '/dataAnalytics/reports/qualityReport' }
    ])
    addGroupRoute('CONFIGURATION', '/configuration', [
      { permissionCode: 'ASSETS', path: '/configuration/assets' },
      { permissionCode: 'FIXTURE TYPES', path: '/configuration/fixtureTypes' },
      { permissionCode: 'SENSORS & ACTUATORS', path: '/configuration/sensorsActuators' },
      { permissionCode: 'TECHNOLOGY', path: '/configuration/technology' },
      { permissionCode: 'TOOLS_MANAGER', path: '/configuration/toolGroups' },
      { permissionCode: 'CALENDARS', path: '/configuration/calendars' },
      { permissionCode: 'PICKLIST', path: '/configuration/picklist' },
      { permissionCode: 'CLIENT', path: '/configuration/customer' },
      { permissionCode: 'WORKERS', path: '/configuration/workers' },
      { permissionCode: 'MATERIALS', path: '/configuration/materials' },
      { permissionCode: 'DOCUMENT_REPOSITORY', path: '/configuration/documentRepository' },
      { permissionCode: 'GENERATE_QR_CODE', path: '/configuration/printers' }
    ])
    addChildPermissionGroupRoute('/header', [
      { permissionCode: 'USERS', path: '/users' },
      { permissionCode: 'PROFILES', path: '/profiles' },
      { permissionCode: 'SETTINGS', path: '/settings' }
    ], false, 'header')
    // Story 213 (GATE 2 bis, C12.4, H8): Camera control lives in BACKOFFICE (below, SUPERADMIN only); its old route under
    // Settings only redirects there. Explicitly gated so SETTINGS never inherits either through a shared prefix.
    explicitlyGatedPaths.add('/settings/cameraControl')
    explicitlyGatedPaths.add('/backoffice/cameraControl')
    addChildPermissionGroupRoute('/cluster', [
      { permissionCode: 'PENDING_SYNC_REQUESTS', path: '/cluster/pendingRequests' },
      { permissionCode: 'HISTORIC_SYNC_REQUESTS', path: '/cluster/historicRequests' },
      { permissionCode: 'CLUSTER_SETTINGS', path: '/cluster/settings' }
    ], false, 'header')
    addGroupRoute('MAINTENANCE', '/maintenance', [
      { permissionCode: 'ALARMS', path: '/maintenance/alarms' },
      { permissionCode: 'NOTIFICATIONS', path: '/maintenance/notifications' }
    ])
    // Story 213 (S118, B-R3-4, PO 2026-10-20): Vision incidents sits under Maintenance in the menu but needs SETTINGS only,
    // not MAINTENANCE; explicitly gated so MAINTENANCE alone never inherits it through the /maintenance prefix.
    explicitlyGatedPaths.add('/maintenance/visionIncidents')
    addRoute('SETTINGS', '/maintenance/visionIncidents', false)
    // D3/RN1 — Quality is now a group entry with 2 independent sub-routes: "Quality" (permission
    // QUALITY) and "Product Acceptance (MOD)" (permission FABRICATION_ORDER_ACCEPTANCE + flag).
    // Using addChildPermissionGroupRoute (not addRoute/addGroupRoute) means the group's visibility
    // and firstPath depend only on its children, so a QUALITY-only user keeps full access to
    // Quality Incidence/Actions (RN5/CA9) and a FABRICATION_ORDER_ACCEPTANCE-only user (flag on)
    // still reaches the acceptance route directly, independent of the QUALITY permission (CA1/CA4).
    addChildPermissionGroupRoute('/quality', [
      { permissionCode: 'QUALITY', path: '/quality' },
      { permissionCode: 'FABRICATION_ORDER_ACCEPTANCE', path: '/quality/productAcceptance', enabled: acceptanceEnabled }
    ])
    addChildPermissionGroupRoute('/mos', [
      { permissionCode: 'RECEPTION', path: '/mos/reception' },
      { permissionCode: 'MANUFACTURING', path: '/mos/manufacturing' },
      { permissionCode: 'DATA_CORRECTION_VIEW', path: '/mos/dataCorrection' }
    ])

    // Backoffice is gated by SUPERADMIN profile code (D1/P5), not by a permission code. It is a
    // full screen reachable by URL (must survive a reload), so it lives in the 'main' section but
    // stays hidden from the navigation bar (showInMainMenu: false). Using the 'header' section
    // would make a direct load / reload (navigationType 'POP') bounce back to the main nav.
    // Story 213 (C14.3, DR-3, PO 2026-10-22): the profile ADMIN reaches Camera control only, never the rest of BACKOFFICE.
    if (isAdmin) {
      entries.push({
        firstPath: '/backoffice/cameraControl',
        paths: ['/backoffice/cameraControl', '/settings/cameraControl'],
        showInMainMenu: false,
        mainMenuItems: 1,
        section: 'main'
      })
    }
    if (isSuperAdmin) {
      entries.push({
        firstPath: '/backoffice/proxiedServices',
        // Story 213 (C12.4, H8): Camera control is one more BACKOFFICE screen; its old route is let through to redirect.
        paths: ['/backoffice/proxiedServices', '/backoffice/cameraControl', '/settings/cameraControl'],
        showInMainMenu: false,
        mainMenuItems: 1,
        section: 'main'
      })
      // Debug screens (/debug/deviceBooker, /debug/cellMoves): SUPERADMIN-only, URL-typed
      // tools with no menu entry. Registering the /debug base path authorizes every
      // /debug/* route via isCurrentPathAllowed's startsWith check — without it the guard
      // bounces a direct load straight to home. The screens gate themselves by profile too.
      entries.push({
        firstPath: '/debug/deviceBooker',
        paths: ['/debug'],
        showInMainMenu: false,
        mainMenuItems: 1,
        section: 'main'
      })
    }

    return entries
  }, [isUserAuthorized, isSuperAdmin, isAdmin, acceptanceEnabled, explicitlyGatedPaths])

  const authorizedMainNavigationEntries = useMemo(() => {
    return authorizedMainMenuEntries.filter(({ section }) => section === 'main')
  }, [authorizedMainMenuEntries])
  const authorizedMainNavigationPaths = useMemo(() => {
    return authorizedMainNavigationEntries.flatMap(({ paths }) => paths)
  }, [authorizedMainNavigationEntries])
  const authorizedHeaderPaths = useMemo(() => {
    return authorizedMainMenuEntries
      .filter(({ section }) => section === 'header')
      .flatMap(({ paths }) => paths)
  }, [authorizedMainMenuEntries])
  const authorizedVisibleMainMenuEntries = useMemo(() => {
    return authorizedMainNavigationEntries.filter(({ showInMainMenu }) => showInMainMenu)
  }, [authorizedMainNavigationEntries])
  const firstAuthorizedMainNavigationRoute = useMemo(() => {
    return authorizedVisibleMainMenuEntries[0]?.firstPath || authorizedMainNavigationEntries[0]?.firstPath
  }, [authorizedMainNavigationEntries, authorizedVisibleMainMenuEntries])
  const firstAuthorizedRoute = firstAuthorizedMainNavigationRoute || authorizedMainMenuEntries[0]?.firstPath
  const hasAuthorizedMainNavigationEntries = authorizedMainNavigationEntries.length > 0
  const hasAuthorizedEntries = authorizedMainMenuEntries.length > 0
  const isCurrentPathAllowed = useCallback((path) => {
    const authorizedPaths = [...authorizedMainNavigationPaths, ...authorizedHeaderPaths]
    if (authorizedPaths.includes(path)) return true
    // An explicitly registered sibling route (e.g. /quality/productAcceptance) must be authorized
    // by its own permission, never inherited through a prefix match with another authorized
    // sibling (e.g. /quality) that merely shares a URL segment.
    if (explicitlyGatedPaths.has(path)) return false
    return authorizedPaths.some(authorizedPath => path.startsWith(`${authorizedPath}/`))
  }, [authorizedHeaderPaths, authorizedMainNavigationPaths, explicitlyGatedPaths])
  const isCurrentPathInHeader = useMemo(() => {
    // A main entry that lives under a header path (story 213: the old /settings/cameraControl, which redirects to BACKOFFICE) is not a header screen.
    if (authorizedMainNavigationPaths.includes(location.pathname)) return false
    return authorizedHeaderPaths.some(path => (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    ))
  }, [authorizedHeaderPaths, authorizedMainNavigationPaths, location.pathname])
  const authorizedVisibleMainMenuItems = useMemo(() => {
    return authorizedVisibleMainMenuEntries.reduce((total, { mainMenuItems }) => total + mainMenuItems, 0)
  }, [authorizedVisibleMainMenuEntries])
  const hideMainMenuBar = authorizedVisibleMainMenuItems <= 1

  useEffect(() => {
    setHideMainMenuBar(hideMainMenuBar)
    setHideMenu(isSmall || hideMainMenuBar)
  }, [hideMainMenuBar, isSmall, setHideMainMenuBar, setHideMenu])

  useEffect(() => {
    if (!user) return

    if (!hasAuthorizedEntries) {
      if (location.pathname !== '/') {
        navigate('/', { replace: true })
      }
      return
    }

    if (location.pathname === '/home') return

    const isInitialRoute = location.pathname === '/'
    const isAllowedRoute = isCurrentPathAllowed(location.pathname)
    const shouldPrioritizeMainNavigation = hasAuthorizedMainNavigationEntries && isCurrentPathInHeader && navigationType === 'POP'
    if (!isInitialRoute && isAllowedRoute && !shouldPrioritizeMainNavigation) return

    navigate(firstAuthorizedRoute, { replace: true })
  }, [firstAuthorizedRoute, hasAuthorizedEntries, hasAuthorizedMainNavigationEntries, isCurrentPathAllowed, isCurrentPathInHeader, location.pathname, navigate, navigationType, user])

  return null
}

export default MainMenuPermissionGuard
